import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export interface InventoryLimit {
  id: string;
  offer_id: string;
  room_type_id: string;
  date: string;
  total: number;
  count: number;
}

export const offerInventoryLimitsQueryKeys = {
  all: ['offerInventoryLimits'] as const,
  forRoomType: (offerId: string, propertyId: string, roomTypeId: string) =>
    [...offerInventoryLimitsQueryKeys.all, offerId, propertyId, roomTypeId] as const,
};

export function useGetOfferInventoryLimits(offerId: string, propertyId: string, roomTypeId: string) {
  return useQuery({
    queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
    queryFn: async ({ signal }) => {
      const response = await ReservationService.getOfferInventoryLimits(offerId, propertyId, roomTypeId, { signal });
      return (response?.result?.limits ?? []) as Array<InventoryLimit>;
    },
    enabled: !!offerId && !!propertyId && !!roomTypeId,
  });
}

export function useSetOfferInventoryLimits(offerId: string, propertyId: string, roomTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { start_date: string; end_date: string; total: number }) =>
      ReservationService.setOfferInventoryLimits(offerId, propertyId, roomTypeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}

export function useSetBulkOfferInventoryLimits(offerId: string, propertyId: string, roomTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { dates: Array<string>; total: number }) =>
      ReservationService.setBulkOfferInventoryLimits(offerId, propertyId, roomTypeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}

export function useDeleteOfferInventoryLimits(offerId: string, propertyId: string, roomTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { start_date?: string; end_date?: string }) =>
      ReservationService.deleteOfferInventoryLimits(
        offerId,
        propertyId,
        roomTypeId,
        params?.start_date,
        params?.end_date,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}
